/**
 * Dealer Detail Page with Tabs
 * Phase 2E: Master Admin UI
 * Phase 2E-P0: Deep-link + refresh resilience with query params
 * 
 * Tabs:
 * - Overview: public_key, embed snippet, stats, status toggle, verify embed
 * - Unlock Branding: configure UnlockSettings with live preview
 * - Smart Return Offers: configure return visitor offers
 * - Users & Invites: manage users and invitations
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Skeleton } from "../../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Trash2 } from "lucide-react";
import DealerOverviewTab from "../../components/admin/DealerOverviewTab";
import DealerBrandingTab from "../../components/admin/DealerBrandingTab";
import DealerSmartOffersTab from "../../components/admin/DealerSmartOffersTab";
import DealerUsersTab from "../../components/admin/DealerUsersTab";
import DealerProductsTab from "../../components/admin/DealerProductsTab";
import Answer24ConnectionPanel from "../../components/admin/Answer24ConnectionPanel";
import PopupCoordinationPanel from "../../components/admin/PopupCoordinationPanel";
import RecallCheckAdminPanel from "../../recall/RecallCheckAdminPanel";
import BrochureBuilderAdminPanel from "../../brochure/BrochureBuilderAdminPanel";
import IdentityMatchAdminPanel from "../../components/admin/IdentityMatchAdminPanel";
import PricingRulesPage from "../PricingRulesPage";

const AdminDealerDetailPage = ({ dealerId, onNavigate, initialTab = "overview", onTabChange }) => {
  const { apiFetch, user } = useAuth();
  const { success: showToast, error: showError } = useToast();
  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Sync tab with URL when initialTab changes
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab]);

  // If a deep-link puts us on Pricing (New) but franchise isn't set OR dealer
  // is Used Cars Only, fall back to Overview.
  useEffect(() => {
    if (!dealer || activeTab !== "pricing-rules-new") return;
    const isUsedOnly = dealer.franchise_make === "USED_CARS_ONLY";
    if (isUsedOnly || !dealer.franchise_make) {
      setActiveTab("overview");
      if (onTabChange) onTabChange("overview");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealer, activeTab]);

  const fetchDealer = useCallback(async () => {
    if (!dealerId) {
      setError("No dealer ID provided");
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch(`/admin/dealers/${dealerId}`);
      setDealer(response.data);
    } catch (err) {
      const errorMsg = err.response?.status === 404 
        ? "Dealer not found" 
        : "Failed to load dealer details";
      setError(errorMsg);
      showError(errorMsg);
      console.error("[AdminDealerDetailPage] FETCH FAILED:", err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, dealerId, showError]);

  useEffect(() => {
    fetchDealer();
  }, [fetchDealer]);

  const handleTabChange = (tab) => {
    // Block Pricing (New) when no franchise is set OR dealer is Used-Cars-Only
    const isUsedOnly = dealer?.franchise_make === "USED_CARS_ONLY";
    if (tab === "pricing-rules-new" && (isUsedOnly || !dealer?.franchise_make)) {
      showError(
        isUsedOnly
          ? "This dealer is configured as Used Cars Only — only Pricing (Used) is available."
          : "Set a Franchise Make on the Overview tab before configuring New Vehicle pricing."
      );
      return;
    }
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  const handleToggleStatus = async () => {
    if (!dealer) return;
    try {
      await apiFetch(`/admin/dealers/${dealer.id}`, {
        method: "PUT",
        data: { is_active: !dealer.is_active }
      });
      showToast(`Dealer ${dealer.is_active ? "disabled" : "enabled"} successfully`);
      fetchDealer();
    } catch (err) {
      showError("Failed to update dealer status");
    }
  };

  const handleDeleteDealer = async () => {
    if (!dealer) return;
    setDeleting(true);
    try {
      await apiFetch(`/admin/dealers/${dealer.id}`, { method: "DELETE" });
      showToast(`Dealer '${dealer.name}' permanently deleted`);
      setDeleteOpen(false);
      onNavigate("admin-dealers");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to delete dealer";
      showError(msg);
    } finally {
      setDeleting(false);
    }
  };

  // RBAC check
  if (user?.role !== "MASTER_ADMIN") {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="py-16 text-center">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don&apos;t have permission to view dealer details.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="p-6 w-full">
        <button
          onClick={() => onNavigate("admin-dealers")}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 text-sm"
        >
          <span className="mr-2">←</span> Back to Dealers
        </button>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-14 w-14 rounded-xl" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <Skeleton className="h-12 w-full max-w-md" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  // Error state - dealer not found
  if (error || !dealer) {
    return (
      <div className="p-8">
        <button
          onClick={() => onNavigate("admin-dealers")}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 text-sm"
        >
          <span className="mr-2">←</span> Back to Dealers
        </button>
        <Card>
          <CardContent className="py-16 text-center">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {error === "Dealer not found" ? "Dealer Not Found" : "Error Loading Dealer"}
            </h2>
            <p className="text-gray-600 mb-4">
              {error === "Dealer not found" 
                ? "The requested dealer could not be found. It may have been deleted."
                : error || "An unexpected error occurred."}
            </p>
            <Button onClick={() => onNavigate("admin-dealers")}>Back to Dealers</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 w-full">
      {/* Back Button */}
      <button
        onClick={() => onNavigate("admin-dealers")}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6 text-sm"
      >
        <span className="mr-2">←</span> Back to Dealers
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xl">
            {dealer.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-dealer-header-name">
                {dealer.name}
                {dealer.franchise_make ? (
                  <span className="text-gray-400 font-normal ml-2" data-testid="admin-dealer-header-franchise">
                    · {dealer.franchise_make}
                  </span>
                ) : (
                  <span className="text-gray-300 italic font-normal ml-2" data-testid="admin-dealer-header-franchise-missing">
                    · No Franchise Set
                  </span>
                )}
              </h1>
              <Badge 
                variant={dealer.is_active ? "default" : "secondary"} 
                className={dealer.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}
              >
                {dealer.is_active ? "Active" : "Disabled"}
              </Badge>
            </div>
            <p className="text-gray-500 text-sm mt-1">
              Created {dealer.created_at ? new Date(dealer.created_at).toLocaleDateString() : "N/A"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={dealer.is_active ? "outline" : "default"}
            onClick={handleToggleStatus}
            className={dealer.is_active ? "text-red-600 hover:text-red-700 hover:bg-red-50" : "bg-green-600 hover:bg-green-700"}
            data-testid="admin-dealer-disable-btn"
          >
            {dealer.is_active ? "Disable Dealer" : "Enable Dealer"}
          </Button>
          <Button
            onClick={() => setDeleteOpen(true)}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="admin-dealer-delete-btn"
          >
            <Trash2 size={14} className="mr-1.5" /> Delete Dealer
          </Button>
        </div>
      </div>

      {/* Disabled Warning */}
      {!dealer.is_active && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-red-800">
            <span>⚠️</span>
            <span className="font-medium">This dealer is disabled.</span>
          </div>
          <p className="text-red-700 text-sm mt-1">
            The embed script won&apos;t work, users cannot be invited, and existing invites cannot be accepted.
          </p>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="bg-gray-100 p-1 rounded-lg">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white rounded-md px-4 py-2">
            📋 Overview
          </TabsTrigger>
          <TabsTrigger value="branding" className="data-[state=active]:bg-white rounded-md px-4 py-2">
            🎨 Unlock Branding
          </TabsTrigger>
          <TabsTrigger value="smart-offers" className="data-[state=active]:bg-white rounded-md px-4 py-2">
            🔔 Popup Offers
          </TabsTrigger>
          {dealer.franchise_make !== "USED_CARS_ONLY" && (
            <TabsTrigger
              value="pricing-rules-new"
              className="data-[state=active]:bg-white rounded-md px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="admin-dealer-tab-pricing-new"
              disabled={!dealer.franchise_make}
              title={
                !dealer.franchise_make
                  ? "Set a Franchise Make on the Overview tab to configure New Vehicle pricing rules."
                  : undefined
              }
            >
              💲 Pricing (New)
              {!dealer.franchise_make && (
                <span className="ml-1 text-amber-500" aria-hidden="true">⚠</span>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="pricing-rules-used" className="data-[state=active]:bg-white rounded-md px-4 py-2" data-testid="admin-dealer-tab-pricing-used">
            💲 Pricing (Used)
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-white rounded-md px-4 py-2">
            👥 Users & Invites
          </TabsTrigger>
          <TabsTrigger value="products" className="data-[state=active]:bg-white rounded-md px-4 py-2" data-testid="admin-dealer-tab-products">
            📦 Products
          </TabsTrigger>
          <TabsTrigger value="answer24" className="data-[state=active]:bg-white rounded-md px-4 py-2" data-testid="admin-dealer-tab-answer24">
            🔗 Answer24
          </TabsTrigger>
          <TabsTrigger value="recall-check" className="data-[state=active]:bg-white rounded-md px-4 py-2" data-testid="admin-dealer-tab-recall">
            🛡 Recall Check
          </TabsTrigger>
          <TabsTrigger value="popup-coord" className="data-[state=active]:bg-white rounded-md px-4 py-2" data-testid="admin-dealer-tab-popup-coord">
            🎯 Popup Coordination
          </TabsTrigger>
          <TabsTrigger value="brochure" className="data-[state=active]:bg-white rounded-md px-4 py-2" data-testid="admin-dealer-tab-brochure">
            📄 Brochure Builder
          </TabsTrigger>
          <TabsTrigger value="identity-match" className="data-[state=active]:bg-white rounded-md px-4 py-2" data-testid="admin-dealer-tab-identity-match">
            🔍 Identity Match
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <DealerOverviewTab dealer={dealer} onRefresh={fetchDealer} />
        </TabsContent>

        <TabsContent value="branding">
          <DealerBrandingTab 
            dealer={dealer} 
            unlockSettings={dealer.unlock_settings} 
            onRefresh={fetchDealer}
            disabled={!dealer.is_active}
          />
        </TabsContent>

        <TabsContent value="smart-offers">
          <DealerSmartOffersTab 
            dealer={dealer}
            onRefresh={fetchDealer}
            disabled={!dealer.is_active}
          />
        </TabsContent>

        <TabsContent value="pricing-rules-new" data-testid="admin-dealer-pricing-new-tab">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h3 className="text-[15px] font-semibold text-gray-900 mb-1">Pricing Rules — New Vehicles</h3>
            <p className="text-[12px] text-gray-500 mb-4">
              Rules applied to this dealer&rsquo;s new-vehicle unlocks.
            </p>
            <PricingRulesPage condition="new" businessId={dealer.id} compact />
          </div>
        </TabsContent>

        <TabsContent value="pricing-rules-used" data-testid="admin-dealer-pricing-used-tab">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h3 className="text-[15px] font-semibold text-gray-900 mb-1">Pricing Rules — Used Vehicles</h3>
            <p className="text-[12px] text-gray-500 mb-4">
              Rules applied to this dealer&rsquo;s used-vehicle unlocks.
            </p>
            <PricingRulesPage condition="used" businessId={dealer.id} compact />
          </div>
        </TabsContent>

        <TabsContent value="users">
          <DealerUsersTab 
            dealer={dealer}
            users={dealer.users || []}
            pendingInvites={dealer.pending_invites || []}
            onRefresh={fetchDealer}
            disabled={!dealer.is_active}
          />
        </TabsContent>

        <TabsContent value="products" data-testid="admin-dealer-products-tab-panel">
          <DealerProductsTab dealer={dealer} onRefresh={fetchDealer} />
        </TabsContent>

        <TabsContent value="answer24" data-testid="admin-dealer-answer24-tab-panel">
          <Answer24ConnectionPanel dealer={dealer} />
        </TabsContent>

        <TabsContent value="recall-check" data-testid="admin-dealer-recall-tab-panel">
          <RecallCheckAdminPanel dealer={dealer} />
        </TabsContent>

        <TabsContent value="popup-coord" data-testid="admin-dealer-popup-coord-tab-panel">
          <PopupCoordinationPanel dealer={dealer} />
        </TabsContent>

        <TabsContent value="brochure" data-testid="admin-dealer-brochure-tab-panel">
          <BrochureBuilderAdminPanel dealer={dealer} />
        </TabsContent>

        <TabsContent value="identity-match" data-testid="admin-dealer-identity-match-tab-panel">
          <IdentityMatchAdminPanel dealer={dealer} onRefresh={fetchDealer} />
        </TabsContent>
      </Tabs>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent data-testid="delete-dealer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 size={18} /> Permanently Delete Dealer
            </DialogTitle>
            <DialogDescription className="pt-2 text-gray-700">
              Are you sure you want to permanently delete <span className="font-semibold">{dealer.name}</span>? This will delete all associated leads, pricing rules, unlock grants, and settings. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
              data-testid="delete-dealer-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteDealer}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-dealer-confirm"
            >
              {deleting ? "Deleting…" : "Permanently Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDealerDetailPage;
